/*
 * PSYKHE PLP Analytics Tracking — Olivela Shopify Theme
 *
 * Loaded by psykhe.js after page parse. Depends on globals that psykhe.js
 * defines first:
 *   - olivelaRuntimeState        — instance id + facets array
 *   - isOlivelaPlpEvent()        — instance guard for PSYKHE events
 *   - getOlivelaCollectionTitle()— page collection title for sort label
 *   - normalizeOlivelaHierarchicalLabel() — "A > B" → "B" for chip labels
 *   - getOlivelaPriorityFacetLabel()      — facetKey → display label
 *
 * Tracking coverage:
 *   1. Filter apply / remove   — psykhe-facet-change + psykhe-filter-remove
 *   2. Chip removal            — DOM click on psykhe-active-filters host
 *   3. Clear all filters       — psykhe-filters-cleared + DOM click
 *   4. Sort selection          — psykhe-sort-change + DOM click fallback
 */

// ---------------------------------------------------------------------------
// CORE PUSH HELPER
// ---------------------------------------------------------------------------

// Safe shims for globals exposed by psykhe.js (type="module").
// Prefixed with _ so the function names don't shadow the window properties
// they delegate to (which would cause infinite recursion in plain scripts).
function _getOlivelaCollectionTitle() {
  // Read window.COLLECTION_TITLE directly (set by theme.liquid) so this always
  // works regardless of whether psykhe.js has executed yet.
  if (typeof window.COLLECTION_TITLE === 'string' && window.COLLECTION_TITLE.trim()) {
    var decoder = document.createElement('textarea');
    decoder.innerHTML = window.COLLECTION_TITLE.trim();
    return decoder.value.trim();
  }
  // Fallback to psykhe.js helper if available.
  return typeof window.getOlivelaCollectionTitle === 'function'
    ? window.getOlivelaCollectionTitle() : '';
}
function _normalizeOlivelaHierarchicalLabel(label) {
  return typeof window.normalizeOlivelaHierarchicalLabel === 'function'
    ? window.normalizeOlivelaHierarchicalLabel(label) : String(label || '');
}
function _getOlivelaPriorityFacetLabel(key) {
  return typeof window.getOlivelaPriorityFacetLabel === 'function'
    ? window.getOlivelaPriorityFacetLabel(key) : '';
}

// Local instance guard — independent of psykhe.js's cached isOlivelaPlpEvent.
// Accepts events where PSYKHE fires no instance (empty string) or where the
// instance matches olivelaRuntimeState.instance.
function _isPlpEvent(detail) {
  var inst = String((detail && typeof detail.instance === 'string' ? detail.instance : '') || '').trim();
  if (inst === '') return true; // no instance in event — accept
  var state = (typeof olivelaRuntimeState !== 'undefined' && olivelaRuntimeState) ||
              (typeof window !== 'undefined' && window.olivelaRuntimeState);
  return !state || inst === state.instance;
}

function pushOlivelaDataLayer(payload) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);

  var reset = {};
  Object.keys(payload).forEach(function (key) {
    if (key !== "event") {
      reset[key] = undefined;
    }
  });
  window.dataLayer.push(reset);
}


// ---------------------------------------------------------------------------
// FILTER TRACKING HELPERS
// ---------------------------------------------------------------------------

function psykheFilterType(facetKey) {
  switch (String(facetKey || "").toLowerCase()) {
    case "category_0":
      return "Type";
    case "category_1":
      return "Cat";
    case "category_2":
      return "Subcat";
    case "color":
      return "Color";
    case "size":
    case "us_size":
      return "Size";
    case "material":
      return "Material";
    case "occasion":
      return "Occasion";
    case "stone":
      return "Gemstone";
    case "availability":
      return "Availability";
    case "designer":
    case "vendor":
    case "brand":
      return "Designer";
    default:
      return facetKey || "";
  }
}

// Maps chip-facet-label display text (e.g. "Category 1:") to a facet key
// (e.g. "category_1"). Used by the chip-removal DOM listener where
// composedPath() gives us the label text rather than the raw key.
function psykheChipFacetLabelToKey(rawLabel) {
  var normalized = String(rawLabel || "")
    .trim()
    .toLowerCase()
    .replace(/:$/, "")
    .trim();
  switch (normalized) {
    case "category":
    case "category 0":
      return "category_0";
    case "category 1":
      return "category_1";
    case "category 2":
      return "category_2";
    case "designer":
      return "designer";
    case "vendor":
      return "vendor";
    case "brand":
      return "brand";
    case "size":
      return "size";
    case "us size":
      return "us_size";
    case "color":
      return "color";
    case "material":
      return "material";
    case "occasion":
      return "occasion";
    case "stone":
      return "Gemstone";
    case "availability":
      return "availability";
    default:
      return normalized.replace(/\s+/g, "_");
  }
}

// Push-level dedup — rejects the same filter action pushed twice within 2 s.
// Handles race conditions where both DOM fallback and PSYKHE event fire.
var _lastFilterPushKey = '';
var _lastFilterPushTs = 0;

function psykhePLPFiltersTracking(facetKey, value, isRefined) {
  var dedupKey = (isRefined ? '1' : '0') + '|' + String(facetKey || '') + '|' + String(value || '');
  var now = Date.now();
  if (dedupKey === _lastFilterPushKey && now - _lastFilterPushTs < 2000) return;
  _lastFilterPushKey = dedupKey;
  _lastFilterPushTs = now;

  var label = psykheFilterType(facetKey);
  var trackingLabel = isRefined ? "Clear ~ " : "";
  trackingLabel += label === "Brand" ? "Designers" : label;
  if (value) {
    trackingLabel += " - " + value;
  }

  pushOlivelaDataLayer({
    event: "applyPLPFilter",
    label: trackingLabel,
    filterlabel: trackingLabel,
  });
}

// ---------------------------------------------------------------------------
// SORT TRACKING HELPERS
// ---------------------------------------------------------------------------

function trackOlivelaSortSelection(label) {
  var normalizedLabel = String(label || "").trim();
  if (!normalizedLabel) return;

  var collectionTitle = _getOlivelaCollectionTitle();

  var sortLabel =
    "Sort - " +
    (collectionTitle ? collectionTitle + " - " : "") +
    normalizedLabel;

  pushOlivelaDataLayer({
    event: "applyPLPSort",
    label: sortLabel,
    sortlabel: sortLabel,
  });
}

// ---------------------------------------------------------------------------
// PSYKHE EVENT HANDLERS
// ---------------------------------------------------------------------------

// Pending DOM-fallback push — cancelled by handleOlivelaFacetChange if
// psykhe-facet-change fires within 200 ms (prevents double-tracking).
var _pendingFilterTrackTimeoutId = 0;
var _pendingFilterTrack = null;

// Dedup for chip-removal: psykhe-filter-remove and psykhe-facet-change both
// fire when a chip is clicked. psykhe-filter-remove is authoritative — it
// stamps the facetKey + Date.now() so the deferred psykhe-facet-change
// callback can check and skip if filter-remove already handled it.
var _lastFilterRemoveFacetKey = '';
var _lastFilterRemoveTs = 0;

// Dedup for psykhe-facet-cleared: PSYKHE fires both psykhe-facet-change
// (selected=false) and psykhe-facet-cleared when the last value in a facet
// is unchecked via the panel. Stamp the facetKey here so handleOlivelaFacetCleared
// can skip if psykhe-facet-change already handled the same facet within 200 ms.
var _lastFacetChangeFacetKey = '';
var _lastFacetChangeTs = 0;

function handleOlivelaFacetChange(event) {
  var detail = event.detail || {};
  if (!_isPlpEvent(detail)) return;

  // Cancel DOM-click fallback — psykhe-facet-change is authoritative over DOM clicks.
  if (_pendingFilterTrackTimeoutId) {
    window.clearTimeout(_pendingFilterTrackTimeoutId);
    _pendingFilterTrackTimeoutId = 0;
  }
  _pendingFilterTrack = null;

  // For removals (selected === false): defer 50 ms so psykhe-filter-remove
  // (which fires alongside this for chip removals) can stamp first and cancel.
  // For applies (selected === true): push immediately — no filter-remove fires.
  if (detail.selected === false) {
    var _facetKey = detail.facetKey;
    var _facetValue = String(detail.value != null ? detail.value : "");
    // Stamp so handleOlivelaFacetCleared can suppress its push for the same facet.
    _lastFacetChangeFacetKey = _facetKey;
    _lastFacetChangeTs = Date.now();
    window.setTimeout(function () {
      if (
        _facetKey === _lastFilterRemoveFacetKey &&
        Date.now() - _lastFilterRemoveTs < 200
      ) {
        return;
      }
      psykhePLPFiltersTracking(_facetKey, _facetValue, true);
    }, 50);
    return;
  }

  psykhePLPFiltersTracking(
    detail.facetKey,
    String(detail.value != null ? detail.value : ""),
    false
  );
}

function handleOlivelaFacetCleared(event) {
  var detail = event.detail || {};
  if (!_isPlpEvent(detail)) return;
  var now = Date.now();
  // Skip if psykhe-filter-remove already handled this facet within 200 ms.
  if (detail.facetKey === _lastFilterRemoveFacetKey && now - _lastFilterRemoveTs < 200) return;
  // Skip if psykhe-facet-change (selected=false) already handled this facet within 200 ms.
  if (detail.facetKey === _lastFacetChangeFacetKey && now - _lastFacetChangeTs < 200) return;
  psykhePLPFiltersTracking(detail.facetKey, "", true);
}

function handleOlivelaFilterRemove(event) {
  var detail = event.detail || {};
  if (!_isPlpEvent(detail)) return;

  // Stamp this removal so handleOlivelaFacetChange can skip the duplicate.
  _lastFilterRemoveFacetKey = detail.facetKey;
  _lastFilterRemoveTs = Date.now();

  // detail.label can include the facet display name as a prefix (e.g. "Occasion: Vacation").
  // Strip it by taking only the part after the last ": " if present, then fall
  // back to detail.value which is always the raw value.
  var rawLabel = String(detail.label || detail.value || '').trim();
  var colonIdx = rawLabel.lastIndexOf(': ');
  var value = colonIdx !== -1 ? rawLabel.slice(colonIdx + 2).trim() : rawLabel;
  if (!value) value = String(detail.value || '').trim();

  psykhePLPFiltersTracking(detail.facetKey, value, true);
}

function handleOlivelaFiltersCleared(event) {
  var detail = event.detail || {};
  if (!_isPlpEvent(detail)) return;

  var payload = {
    event: "applyPLPFilter",
    label: "Clear ~ All Filters",
    filterlabel: "Clear ~ All Filters",
  };
  pushOlivelaDataLayer(payload);
}

// Pending sort fallback — cancelled by handleOlivelaSortChange if
// psykhe-sort-change fires within 200 ms.
var _pendingSortTrackTimeoutId = 0;
var _pendingSortTrack = null;

function handleOlivelaSortChange(event) {
  var detail = event.detail || {};
  if (!_isPlpEvent(detail)) return;

  // PSYKHE may use detail.label, detail.value, or detail.sort for the label.
  var label = String(detail.label || detail.value || detail.sort || '').trim();
  if (!label) {
    // No usable label in the event — leave the DOM click fallback running.
    return;
  }

  // We have a label: cancel DOM fallback and push from the event.
  if (_pendingSortTrackTimeoutId) {
    window.clearTimeout(_pendingSortTrackTimeoutId);
    _pendingSortTrackTimeoutId = 0;
  }
  _pendingSortTrack = null;

  trackOlivelaSortSelection(label);
}

// ---------------------------------------------------------------------------
// DOM-LEVEL CHIP REMOVAL TRACKING
// Attaches to psykhe-active-filters host via capture + composedPath().
// Also shortens hierarchical labels ("A > B" → "B") via MutationObserver.
// ---------------------------------------------------------------------------

function initOlivelaActiveFilterChipClicks(activeFilters) {
  if (!(activeFilters instanceof HTMLElement)) return;
  if (activeFilters.dataset.psykheOlivelaChipTrackingInit === "true") return;
  activeFilters.dataset.psykheOlivelaChipTrackingInit = "true";

  function syncChipLabel(valueSpan) {
    if (!(valueSpan instanceof HTMLElement)) return;
    var raw = valueSpan.dataset.psykheOlivelaOriginalLabel;
    if (!raw) {
      raw = valueSpan.textContent || "";
      if (raw.indexOf(">") !== -1) {
        valueSpan.dataset.psykheOlivelaOriginalLabel = raw;
      }
    }
    if (!raw || raw.indexOf(">") === -1) return;
    var shortened = _normalizeOlivelaHierarchicalLabel(raw);
    if (valueSpan.textContent !== shortened) valueSpan.textContent = shortened;
  }

  var _chipLabelObserver = null;

  function syncAllChipLabels() {
    var sr = activeFilters.shadowRoot;
    if (!sr) return;
    sr.querySelectorAll("[part~='chip-value-label']").forEach(syncChipLabel);
    if (_chipLabelObserver) _chipLabelObserver.disconnect();
    _chipLabelObserver = new MutationObserver(function (records) {
      records.forEach(function (record) {
        var target = record.target;
        if (
          target instanceof HTMLElement &&
          (target.getAttribute("part") || "").split(" ").indexOf("chip-value-label") !== -1
        ) {
          syncChipLabel(target);
        } else if (target instanceof Text) {
          var parent = target.parentElement;
          if (
            parent instanceof HTMLElement &&
            (parent.getAttribute("part") || "").split(" ").indexOf("chip-value-label") !== -1
          ) {
            syncChipLabel(parent);
          }
        }
      });
    });
    _chipLabelObserver.observe(sr, { childList: true, subtree: true, characterData: true });
  }

  activeFilters.addEventListener(
    "click",
    function (event) {
      var path = event.composedPath ? event.composedPath() : [];
      var chipButton = null;
      for (var i = 0; i < path.length; i++) {
        var el = path[i];
        if (
          el instanceof HTMLElement &&
          el.tagName === "BUTTON" &&
          (el.getAttribute("part") || "").split(" ").indexOf("chip") !== -1
        ) {
          chipButton = el;
          break;
        }
      }
      if (!chipButton) return;
      // psykhe-filter-remove fires for every chip removal and is handled by
      // handleOlivelaFilterRemove — do not also push here to avoid duplicates.
    },
    true
  );

  activeFilters.addEventListener("psykhe-active-filters-change", function () {
    syncAllChipLabels();
  });

  syncAllChipLabels();
}

// ---------------------------------------------------------------------------
// DOM-LEVEL FILTER PANEL CLICK TRACKING
// Uses a SINGLE document-level capture listener. A label click causes the
// browser to fire TWO click events (label + synthetic checkbox). We collapse
// them with a 50 ms event.timeStamp dedup keyed on facetKey+value+isRefined.
// ---------------------------------------------------------------------------

var _filterDocClickInit = false;
var _lastFilterClickKey = "";
var _lastFilterClickTs = 0;

function initOlivelaFilterPanelClickTracking() {
  if (_filterDocClickInit) return;
  _filterDocClickInit = true;

  document.addEventListener(
    "click",
    function (event) {
      var path = event.composedPath ? event.composedPath() : [];

      // Must be a click inside a psykhe-filter-panel.
      var panel = null;
      for (var i = 0; i < path.length; i++) {
        var el = path[i];
        if (el instanceof HTMLElement && el.tagName === "PSYKHE-FILTER-PANEL") {
          panel = el;
          break;
        }
      }
      if (!panel) return;

      // Instance guard.
      var provider = panel.closest("psykhe-provider");
      var panelInstance = String(
        (provider && (provider.getAttribute("instance") || provider.instance)) || ""
      ).trim();
      var _runtimeState = (typeof olivelaRuntimeState !== 'undefined' && olivelaRuntimeState) || window.olivelaRuntimeState || null;
      if (panelInstance && _runtimeState && panelInstance !== _runtimeState.instance) {
        return;
      }

      // Collect accordion, checkbox and option element from the path.
      var accordion = null;
      var optionEl = null;
      var checkbox = null;
      for (var j = 0; j < path.length; j++) {
        var pe = path[j];
        if (pe === panel) break;
        if (!(pe instanceof HTMLElement)) continue;
        if (pe.tagName === "PSYKHE-ACCORDION" && !accordion) accordion = pe;
        if (pe instanceof HTMLInputElement && pe.type === "checkbox" && !checkbox) checkbox = pe;
        if (!optionEl) {
          var parts = (pe.getAttribute("part") || "").split(" ");
          if (parts.indexOf("option") !== -1 || parts.indexOf("facet-option") !== -1) optionEl = pe;
        }
      }
      if (!accordion) return;

      // Resolve facet key by matching accordion summary label.
      var facetCfg = Array.isArray(panel.facets) ? panel.facets : [];
      var accordionSummary = String(
        accordion.summary || accordion.getAttribute("summary") || ""
      ).trim();
      var facet = null;
      if (accordionSummary) {
        facet = facetCfg.find(function (f) {
          if (!f || !f.key) return false;
          var cfgLabel = typeof f.label === "string" ? f.label.trim() : "";
          var fallbackLabel = _getOlivelaPriorityFacetLabel(f.key);
          return (
            (cfgLabel && cfgLabel === accordionSummary) ||
            (fallbackLabel && fallbackLabel === accordionSummary) ||
            accordionSummary.toLowerCase() === f.key.toLowerCase().replace(/_/g, " ")
          );
        }) || null;
      }
      if (!facet) {
        var visibleFacets = facetCfg.filter(function (f) { return f && f.visible !== false; });
        var accordions = panel.shadowRoot
          ? Array.from(panel.shadowRoot.querySelectorAll("psykhe-accordion"))
          : [];
        var aIdx = accordions.indexOf(accordion);
        facet = aIdx >= 0 ? visibleFacets[aIdx] || null : null;
      }
      var facetKey = (facet && facet.key) || "";
      if (!facetKey) return;

      // Resolve display value from the option element.
      var value = "";
      if (optionEl) {
        var labelEl =
          optionEl.querySelector("[part~='option-label']") ||
          optionEl.querySelector("[part~='label']") ||
          optionEl.querySelector("label") ||
          optionEl.querySelector("span");
        value = labelEl
          ? (labelEl.textContent || "").trim()
          : (optionEl.textContent || "").trim();
      }
      if (!value) return;

      // Determine apply vs remove.
      // click fires AFTER the checkbox is toggled:
      //   checked=true  -> just applied  -> isRefined=false
      //   checked=false -> just removed  -> isRefined=true
      var isRefined;
      if (checkbox) {
        isRefined = !checkbox.checked;
      } else {
        isRefined = !!(
          optionEl &&
          (
            optionEl.getAttribute("aria-checked") === "true" ||
            optionEl.getAttribute("aria-selected") === "true" ||
            optionEl.classList.contains("selected") ||
            optionEl.classList.contains("psykhe-selected") ||
            optionEl.classList.contains("is-selected") ||
            optionEl.classList.contains("active")
          )
        );
      }

      // Collapse label-click + synthetic-checkbox-click pairs that the browser
      // fires for the same user gesture (same resolved key within 50 ms).
      var _clickKey = facetKey + "|" + value + "|" + isRefined;
      if (_clickKey === _lastFilterClickKey && event.timeStamp - _lastFilterClickTs < 50) return;
      _lastFilterClickKey = _clickKey;
      _lastFilterClickTs = event.timeStamp;

      // Deferred fallback -- cancelled by handleOlivelaFacetChange if
      // psykhe-facet-change fires within 200 ms.
      if (_pendingFilterTrackTimeoutId) {
        window.clearTimeout(_pendingFilterTrackTimeoutId);
        _pendingFilterTrackTimeoutId = 0;
      }
      _pendingFilterTrack = { facetKey: facetKey, value: value, isRefined: isRefined };
      _pendingFilterTrackTimeoutId = window.setTimeout(function () {
        _pendingFilterTrackTimeoutId = 0;
        if (!_pendingFilterTrack) return;
        var p = _pendingFilterTrack;
        _pendingFilterTrack = null;
        psykhePLPFiltersTracking(p.facetKey, p.value, p.isRefined);
      }, 200);
    },
    true
  );
}

// ---------------------------------------------------------------------------
// DOM-LEVEL CLEAR ALL FILTERS TRACKING
// ---------------------------------------------------------------------------

function initOlivelaClearFiltersTracking(clearEl) {
  if (!(clearEl instanceof HTMLElement)) return;
  if (clearEl.dataset.psykheOlivelaClearTrackingInit === "true") return;
  clearEl.dataset.psykheOlivelaClearTrackingInit = "true";

  clearEl.addEventListener(
    "click",
    function (event) {
      var path = event.composedPath ? event.composedPath() : [];
      var isButton = false;
      for (var i = 0; i < path.length; i++) {
        var el = path[i];
        if (el === clearEl) break;
        if (
          el instanceof HTMLElement &&
          (el.tagName === "BUTTON" ||
            el.tagName === "A" ||
            (el.getAttribute("role") || "") === "button")
        ) {
          isButton = true;
          break;
        }
      }
      if (!isButton) return;

      var payload = {
        event: "applyPLPFilter",
        label: "Clear ~ All Filters",
        filterlabel: "Clear ~ All Filters",
      };
      pushOlivelaDataLayer(payload);
    },
    true
  );
}

// ---------------------------------------------------------------------------
// DOM-LEVEL SORT TRACKING
// ---------------------------------------------------------------------------

// Dedup for sort-select click — same label within 50 ms = one event.
var _lastSortClickLabel = "";
var _lastSortClickTs = 0;

function initOlivelaSortTracking(sortEl) {
  if (!(sortEl instanceof HTMLElement)) return;
  if (sortEl.dataset.psykheOlivelaSortTrackingInit === "true") return;
  sortEl.dataset.psykheOlivelaSortTrackingInit = "true";

  sortEl.addEventListener(
    "click",
    function (event) {
      var path = event.composedPath ? event.composedPath() : [];
      var optionLabel = "";
      for (var i = 0; i < path.length; i++) {
        var el = path[i];
        if (el === sortEl) break;
        if (!(el instanceof HTMLElement)) continue;
        var parts = (el.getAttribute("part") || "").split(" ");
        if (
          parts.indexOf("option") !== -1 ||
          parts.indexOf("sort-option") !== -1 ||
          parts.indexOf("item") !== -1
        ) {
          optionLabel = (el.textContent || "").trim();
          break;
        }
      }
      if (!optionLabel) return;

      // Collapse synthetic duplicates within 50 ms.
      if (optionLabel === _lastSortClickLabel && event.timeStamp - _lastSortClickTs < 50) return;
      _lastSortClickLabel = optionLabel;
      _lastSortClickTs = event.timeStamp;

      if (_pendingSortTrackTimeoutId) {
        window.clearTimeout(_pendingSortTrackTimeoutId);
        _pendingSortTrackTimeoutId = 0;
      }
      _pendingSortTrack = optionLabel;
      _pendingSortTrackTimeoutId = window.setTimeout(function () {
        _pendingSortTrackTimeoutId = 0;
        if (!_pendingSortTrack) return;
        var lbl = _pendingSortTrack;
        _pendingSortTrack = null;
        trackOlivelaSortSelection(lbl);
      }, 200);
    },
    true
  );
}

// ---------------------------------------------------------------------------
// DOM-LEVEL PRIORITY FILTER LABEL TRACKING
// Delegated click on .psykhe-priority-filter-label spans (e.g. Price, Category).
// ---------------------------------------------------------------------------

var _priorityFilterLabelTrackingInit = false;

function initOlivelaPriorityFilterLabelTracking() {
  if (_priorityFilterLabelTrackingInit) return;
  _priorityFilterLabelTrackingInit = true;

  document.addEventListener(
    'click',
    function (event) {
      var path = event.composedPath ? event.composedPath() : [event.target];
      for (var i = 0; i < path.length; i++) {
        var el = path[i];
        if (el === document || el === window) break;
        if (el instanceof HTMLElement && el.classList.contains('psykhe-priority-filter-label')) {
          var filterLabel = (el.textContent || '').trim();
          pushOlivelaDataLayer({
            event: 'applyPLPFilter',
            label: `Priority - ${filterLabel}`,
            filterlabel: `Priority - ${filterLabel}`,
          });
          return;
        }
      }
    },
    true
  );
}

// ---------------------------------------------------------------------------
// DOM-LEVEL VIEW PRODUCTS CTA TRACKING
// The ".custom-filter-view-btn" button is injected dynamically by
// renderFilterStickyBar in custom-filter.js. Use document-level delegation.
// ---------------------------------------------------------------------------

var _viewProductsCTATrackingInit = false;

function initOlivelaViewProductsCTATracking() {
  if (_viewProductsCTATrackingInit) return;
  _viewProductsCTATrackingInit = true;

  document.addEventListener(
    'click',
    function (event) {
      var path = event.composedPath ? event.composedPath() : [event.target];
      for (var i = 0; i < path.length; i++) {
        var el = path[i];
        if (el === document || el === window) break;
        if (
          el instanceof HTMLElement &&
          (el.classList.contains('custom-filter-view-btn') || el.classList.contains('psykhe__drawer-apply'))
        ) {
          pushOlivelaDataLayer({
            event: 'applyPLPFilter',
            label: 'View Products',
            filterlabel: 'View Products',
          });
          return;
        }
      }
    },
    true
  );
}

// ---------------------------------------------------------------------------
// BOOTSTRAP — runs independently, re-runs on PSYKHE lifecycle events
// ---------------------------------------------------------------------------

// Register PSYKHE event listeners IMMEDIATELY — do not wait for DOMContentLoaded.
// User interaction (clicking a filter) always happens after all scripts load,
// but burying listeners inside a DOMContentLoaded callback can miss events if
// that callback chain fails for any reason.
document.addEventListener('psykhe-facet-change', handleOlivelaFacetChange);
document.addEventListener('psykhe-facet-cleared', handleOlivelaFacetCleared);
document.addEventListener('psykhe-filter-remove', handleOlivelaFilterRemove);
document.addEventListener('psykhe-filters-cleared', handleOlivelaFiltersCleared);
document.addEventListener('psykhe-sort-change', handleOlivelaSortChange);

var _psykheTrackingRuntimeInit = false;

function initOlivelaPLPTrackingRuntime() {
  // Event listeners already registered at top level above — nothing to do here.
  if (_psykheTrackingRuntimeInit) return;
  _psykheTrackingRuntimeInit = true;
}

function initOlivelaPLPTracking() {
  initOlivelaPLPTrackingRuntime();

  // Single document-level listener — no need to iterate panels.
  initOlivelaFilterPanelClickTracking();

  document.querySelectorAll("psykhe-active-filters").forEach(function (af) {
    initOlivelaActiveFilterChipClicks(af);
  });

  document.querySelectorAll("psykhe-clear-filters").forEach(function (el) {
    initOlivelaClearFiltersTracking(el);
  });

  document.querySelectorAll("psykhe-sort-select").forEach(function (el) {
    initOlivelaSortTracking(el);
  });

  initOlivelaViewProductsCTATracking();
  initOlivelaPriorityFilterLabelTracking();
}

var _psykheTrackingRafId = 0;
function scheduleOlivelaPLPTracking() {
  if (_psykheTrackingRafId) return;
  _psykheTrackingRafId = window.requestAnimationFrame(function () {
    _psykheTrackingRafId = 0;
    initOlivelaPLPTracking();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initOlivelaPLPTracking, { once: true });
} else {
  initOlivelaPLPTracking();
}
document.addEventListener("psykhe-instance-ready", scheduleOlivelaPLPTracking);
document.addEventListener("psykhe-results-updated", scheduleOlivelaPLPTracking);
document.addEventListener("psykhe-refinements-change", scheduleOlivelaPLPTracking);
